import styles from './PromoCodeInput.scss';

import FormControl from '@material-ui/core/FormControl';
import TextField from '@material-ui/core/TextField';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCart } from 'src/components/Cart/UseCart';
import { useProductPricing } from 'src/components/ProductPricing/ProductPricing';
import { useSalesChannel } from 'src/components/SalesChannel/SalesChannel';
import { fetchPromotion } from 'src/services/shop/cart';
import { getField } from 'src/utils/EntryUtil';
import { useFetchingPromoContext } from '../FetchingPromoContext/FetchingPromoContext';
import { initialFetchingPromoValue } from '../FetchingPromoContext/UseFetchingPromoInternal';

interface PromoCodeInputProps {
  handleProgramPromo?: () => void;
}

export const PromoCodeInput: FC<PromoCodeInputProps> = ({ handleProgramPromo }) => {
    const { t } = useTranslation();
    const { salesChannel } = useSalesChannel();
    const { cart: { products, bundle }, checkout: { promo: promoInCart } } = useCart();
    const { productPricings : allProductsPricing = [] } = useProductPricing();
    const { setFetchingPromo } = useFetchingPromoContext();

    const [error, setError] = useState<string>();
    const [promoCode, setPromoCode] = useState<string>('');

    const allProductsIncart = [...products.map(p => p.id), ...(bundle?.hardware?.map(h => h.id) || [])];
    const productsPricings = allProductsPricing.filter(product => product && allProductsIncart.includes(product.id));

    const inputRef = React.createRef<HTMLInputElement>();

    useEffect(() => {
      setPromoCode(promoInCart?.applied || '');
    }, [promoInCart.applied]);

    useEffect(() => {
      if (promoInCart.applied || promoInCart.program) {
        if (!inputRef.current) return;

        inputRef.current.value = '';
        if (handleProgramPromo && promoInCart.program) handleProgramPromo();
      }
    }, [promoInCart.applied, promoInCart.program]);

    useEffect(() => {
      if (!salesChannel || !promoCode || promoCode === '' || promoInCart.program === promoCode) return;

      applyPromoCode();
    }, [promoCode, salesChannel, allProductsPricing]);

    const validateUserInput = () => {
      if (!inputRef.current || inputRef.current.value.trim() === '') {
        setError(t('promo-code.required'));
        setPromoCode('');
        return;
      }

      setPromoCode(inputRef.current.value.trim());
    };

    const applyPromoCode = async () => {
      const resellerId = getField(salesChannel, 'cloverResellerId') || '';
      const cartProductIds = productsPricings.reduce<string[]>((ids, p) => {
        if (p) ids.push(p.fdmpId);
        return ids;
      }, []);

      try {
        setFetchingPromo({ status: 'loading', data: null });

        const { data } = await fetchPromotion({
          cartProductIds,
          promoCode,
          resellerId,
        });

        setError('');

        setFetchingPromo({ status: 'success', data });
      } catch (e) {
        setFetchingPromo(initialFetchingPromoValue);

        if (!e.response) {
          setError(t('promo-code.api-error'));
        } else {
          setError(e.response.status === 404 ? t('promo-code.invalid-promo-code') : t('promo-code.api-error'));
        }
      }
    };

    return(
      <FormControl fullWidth>
        <TextField
          id="promoCode"
          name="promoCode"
          inputProps={{
            'data-test': 'promo-code',
            'maxLength': 24,
          }}
          label={t('promo-code.enter-code')}
          helperText={error}
          error={!!error}
          inputRef={inputRef}
          onKeyUp={e => e.key === 'Enter' && validateUserInput()}
        />
        <button
          className={styles.promoInputApplyButton}
          onClick={validateUserInput}
          data-test="apply-button"
        >
          {t('promo-code.apply')}
        </button>
      </FormControl>
    );
};

PromoCodeInput.displayName = 'PromoCodeInput';
